import { useState } from "react";

import { Button } from "@/components/ui/button.tsx";
import { Calendar } from "@/components/ui/calendar.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { ChevronDown } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { COMMIT_BY, commitDays, commitOnDay } from "@/shared/commitDates.ts";
import { COMMIT_TITLE, GOT_IT, IN_PROGRESS_NOW } from "@/shared/taskRowCopy.ts";
import type { UserTask } from "@/types/userTasks.ts";

/**
 * Taking a task on: either the reader is already on it, or they say when it
 * comes back.
 *
 * In progress answers it in a click, which is what a list being worked
 * through needs; the calendar is for a task with a real date. Days rather
 * than times throughout — a commitment is "I will get to this by then", not
 * an appointment. Which days, and whether any fit, is `commitDays`'s to say:
 * for a task due before tomorrow morning the calendar's row is left out, and
 * In progress stands alone.
 */
export const CommitMenu = ({
  task,
  disabled,
  onCommit,
  onHold,
}: {
  task: Pick<UserTask, "dueMs" | "dueDate">;
  disabled: boolean;
  onCommit: (snoozedUntilMs: number) => void;
  /** The reader is on it themselves: the task waits, with them as its holder. */
  onHold: () => void;
}) => {
  const [picking, setPicking] = useState(false);
  // Read as the row draws, for the calendar's bounds.
  const days = commitDays(task);
  return (
    <>
      <DropdownMenu>
        {/* A plain Button, as the Dismiss menu's trigger is: `asChild` clones
            the child with the trigger's own handlers, and `RowButton` takes a
            fixed set of props and would drop them. The chevron says this one
            opens rather than acts, which is what separates it from Done. */}
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 bg-white px-2 text-xs"
            data-statsig-name="tasks-commit"
          >
            {GOT_IT}
            <ChevronDown className="size-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            className="text-xs"
            data-statsig-name="tasks-in-progress"
            onSelect={onHold}
          >
            {IN_PROGRESS_NOW}
          </DropdownMenuItem>
          {days && (
            <DropdownMenuItem
              className="text-xs"
              data-statsig-name="tasks-commit-pick"
              onSelect={() => setPicking(true)}
            >
              {COMMIT_BY}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={picking && days !== null} onOpenChange={setPicking}>
        <DialogContent className="w-auto">
          <DialogHeader>
            <DialogTitle>{COMMIT_TITLE}</DialogTitle>
            <DialogDescription>{GOT_IT}</DialogDescription>
          </DialogHeader>
          <Calendar
            mode="single"
            // Nothing is selected yet: the task has no committed date until
            // this picks one, and showing today as chosen would claim it did.
            onSelect={(day) => {
              if (!day) return;
              setPicking(false);
              onCommit(commitOnDay(day));
            }}
            // From tomorrow, and no later than the task is due: `commitDays`.
            disabled={[
              { before: days?.first ?? new Date() },
              ...(days?.last ? [{ after: days.last }] : []),
            ]}
            defaultMonth={days?.first}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
